import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { MemberSidebarLayout } from './SidebarLayout';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { DollarSign, Loader2, TrendingUp, Store } from 'lucide-react';

export function MROCommissionsPage() {
  const { api } = useAuth();
  const [data, setData] = useState({ commissions: [], total_earned: 0, merchant_bonus: 50 });
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const { data } = await api.get('/api/mro/commissions?limit=50');
      setData(data);
    } catch (e) {
      console.error('Failed to load commissions:', e);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const fmt = (n) => new Intl.NumberFormat('en-PH', { style: 'currency', currency: 'PHP', minimumFractionDigits: 2 }).format(n || 0);
  const bonus = data.merchant_bonus ?? data.rate ?? 50;
  const onboardedCount = data.commissions.filter(c => c.kind === 'onboarding' || c.amount > 0).length;

  if (loading) {
    return (
      <MemberSidebarLayout>
        <div className="min-h-screen flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#00E5FF]" />
        </div>
      </MemberSidebarLayout>
    );
  }

  return (
    <MemberSidebarLayout>
      <div className="p-6 lg:p-8" data-testid="mro-commissions-page">
        <div className="mb-8">
          <h1 className="font-outfit text-3xl font-semibold text-white flex items-center gap-3">
            <DollarSign className="w-8 h-8 text-[#00E676]" /> MRO Commissions
          </h1>
          <p className="text-[#A0B4CC] mt-1">A flat bonus is paid for every merchant you successfully onboard.</p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <Card className="bg-gradient-to-br from-[#041A3B] to-[#0A254D] border-[#00E676]/30 md:col-span-1">
            <CardContent className="p-8">
              <p className="text-[#A0B4CC] mb-2">Total Earned</p>
              <p className="font-outfit text-4xl font-bold text-[#00E676]" data-testid="total-mro-commissions">
                {fmt(data.total_earned)}
              </p>
              <p className="text-[#5C7599] text-xs mt-3">Credited to your wallet&apos;s withdrawable balance</p>
            </CardContent>
          </Card>

          <Card className="bg-[#041A3B] border-white/10">
            <CardContent className="p-8">
              <p className="text-[#A0B4CC] mb-2">Bonus per Merchant</p>
              <p className="font-outfit text-4xl font-bold text-white" data-testid="merchant-bonus">
                {fmt(bonus)}
              </p>
              <p className="text-[#5C7599] text-xs mt-3">Paid once when each recruited merchant is approved.</p>
            </CardContent>
          </Card>

          <Card className="bg-[#041A3B] border-white/10">
            <CardContent className="p-8">
              <p className="text-[#A0B4CC] mb-2">Merchants Onboarded</p>
              <p className="font-outfit text-4xl font-bold text-white" data-testid="onboarded-count">
                {onboardedCount}
              </p>
              <p className="text-[#5C7599] text-xs mt-3">Successfully approved merchant partners.</p>
            </CardContent>
          </Card>
        </div>

        <Card className="bg-[#041A3B] border-white/10">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-[#00E5FF]" /> Onboarding Bonus History
            </CardTitle>
          </CardHeader>
          <CardContent>
            {data.commissions.length === 0 ? (
              <div className="text-center py-12">
                <Store className="w-12 h-12 mx-auto text-[#5C7599] opacity-30 mb-3" />
                <p className="text-[#5C7599] text-sm" data-testid="no-commissions">
                  No bonuses yet. You&apos;ll earn {fmt(bonus)} as soon as the first merchant you recruited is approved.
                </p>
              </div>
            ) : (
              <div className="mobile-scroll-wrap">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-[#5C7599] text-xs uppercase border-b border-white/10">
                      <th className="text-left py-3 px-2">Date</th>
                      <th className="text-left py-3 px-2">Merchant</th>
                      <th className="text-right py-3 px-2">Bonus</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.commissions.map((c) => (
                      <tr key={c.id} className="border-b border-white/5 text-[#A0B4CC]" data-testid={`commission-row-${c.id}`}>
                        <td className="py-3 px-2 text-xs">{new Date(c.created_at).toLocaleDateString('en-PH', { year: 'numeric', month: 'short', day: 'numeric' })}</td>
                        <td className="py-3 px-2">
                          <div>
                            <p className="text-white">{c.merchant_business_name || c.merchant_name}</p>
                            {c.merchant_business_name && <p className="text-xs text-[#5C7599]">{c.merchant_name}</p>}
                          </div>
                        </td>
                        <td className="py-3 px-2 text-right text-[#00E676] font-mono font-medium">{fmt(c.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </MemberSidebarLayout>
  );
}
